// ============================================================================
// OFFBOARD USER MODAL — confirm, run and report an offboard for any leaver.
// Shared by Admin > Users and Admin > Facilitators.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, ExternalLink, Loader, X } from 'lucide-react';
import {
  AffectedProject,
  OffboardResult,
  OwnedProject,
  getOffboardPreview,
  offboardUser
} from '../../services/userManagement';

interface OffboardUserModalProps {
  userId: string;
  displayName: string;
  isFacilitator?: boolean;
  onClose: () => void;
  onOffboarded: (result: OffboardResult) => void;
}

function OwnedProjectList({ projects }: { projects: OwnedProject[] }) {
  return (
    <ul className="list-none pl-0 mt-1 space-y-0.5 max-h-48 overflow-y-auto">
      {projects.map(p => (
        <li key={p.projectId} className="text-xs flex items-center justify-between gap-2">
          <span className="text-amber-700 font-medium truncate">
            • {p.name}{p.status && p.status !== 'active' ? <span className="text-slate-400 font-normal"> ({p.status})</span> : null}
          </span>
          <Link
            to={`/projects/${p.projectId}?tab=access`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-primary hover:underline flex-shrink-0"
          >
            Transfer <ExternalLink className="w-3 h-3" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default function OffboardUserModal({ userId, displayName, isFacilitator, onClose, onOffboarded }: OffboardUserModalProps) {
  const [tokenProjects, setTokenProjects] = useState<AffectedProject[]>([]);
  const [ownedProjects, setOwnedProjects] = useState<OwnedProject[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [offboarding, setOffboarding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OffboardResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    getOffboardPreview(userId)
      .then(preview => {
        if (cancelled) return;
        setTokenProjects(preview.projects);
        setOwnedProjects(preview.ownedProjects);
      })
      .catch(() => { if (!cancelled) setError('Failed to load affected projects'); })
      .finally(() => { if (!cancelled) setLoadingPreview(false); });
    return () => { cancelled = true; };
  }, [userId]);

  function close() {
    if (!offboarding) onClose();
  }

  async function handleConfirm() {
    setOffboarding(true);
    setError(null);
    try {
      const res = await offboardUser(userId);
      setResult(res);
      onOffboarded(res);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to offboard user');
    } finally {
      setOffboarding(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg border border-[#e2e8f0]">
        <div className="flex items-center justify-between p-5 border-b border-[#e2e8f0]">
          <h2 className="font-bold text-navy text-lg">Offboard {displayName}</h2>
          <button onClick={close} disabled={offboarding} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50 disabled:opacity-40">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {result ? (
            <div className="space-y-3">
              <div className={`p-3 rounded-xl border flex items-start gap-2.5 ${result.partial ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                {result.partial
                  ? <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  : <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />}
                <p className={`text-sm font-medium ${result.partial ? 'text-amber-700' : 'text-emerald-700'}`}>
                  {result.partial
                    ? 'Offboarded with some steps failing. Platform and MCP access are blocked; review below and retry as needed.'
                    : 'Account suspended, signed out and all their MCP tokens revoked.'}
                </p>
              </div>
              <ul className="text-xs text-slate-500 space-y-1">
                <li>Account suspended: {result.cognitoSuspended ? '✓' : '✗ failed'}</li>
                <li>Forced sign-out: {result.globalSignOut ? '✓' : '✗ failed'}</li>
                <li>Personal calendar token disabled: {result.personalTokenRotated ? '✓' : '✗ failed'}</li>
                <li>Project MCP tokens revoked: {result.projectTokensRevoked ? '✓' : '✗ failed'}</li>
              </ul>
              {result.projects.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1.5">Projects where their tokens were revoked</p>
                  <ul className="text-xs text-slate-500 space-y-1">
                    {result.projects.map(p => <li key={p.projectId}>{p.name}</li>)}
                  </ul>
                </div>
              )}
              {result.ownedProjects.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1.5">
                    {result.ownedProjects.length} project{result.ownedProjects.length === 1 ? '' : 's'} still owned by {displayName}. Transfer each to a new owner:
                  </p>
                  <OwnedProjectList projects={result.ownedProjects} />
                </div>
              )}
              <button
                onClick={close}
                className="w-full py-2.5 rounded-xl bg-navy text-white text-sm font-bold hover:bg-navy/90 transition-all"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600">This will, in one action:</p>
              <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1">
                <li>Suspend the account and force an immediate sign-out</li>
                {isFacilitator && <li>Grey them out of the studio-booking swim lane (past bookings stay visible)</li>}
                <li>Disable their personal calendar MCP token</li>
                {loadingPreview ? (
                  <li className="flex items-center gap-1.5"><Loader className="w-3 h-3 animate-spin" /> Checking affected projects…</li>
                ) : tokenProjects.length > 0 ? (
                  <li>
                    Revoke their own MCP tokens on {tokenProjects.length} project{tokenProjects.length === 1 ? '' : 's'}:
                    <ul className="list-none pl-0 mt-1 space-y-0.5">
                      {tokenProjects.map(p => (
                        <li key={p.projectId} className="text-xs text-amber-700 font-medium">• {p.name}</li>
                      ))}
                    </ul>
                    <span className="block mt-1 text-xs text-slate-500">
                      Other members' tokens are unaffected.
                    </span>
                  </li>
                ) : (
                  <li>No project MCP tokens are affected, as they don't hold any</li>
                )}
              </ul>
              {!loadingPreview && ownedProjects.length > 0 && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                  <p className="text-xs font-semibold text-amber-800">
                    They own {ownedProjects.length} project{ownedProjects.length === 1 ? '' : 's'}. Ownership does not move automatically; transfer each one before or after offboarding.
                  </p>
                  <OwnedProjectList projects={ownedProjects} />
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={close}
                  disabled={offboarding}
                  className="flex-1 py-2.5 border border-[#e2e8f0] rounded-xl text-sm font-medium text-slate-500 hover:bg-slate-50 transition-colors disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={offboarding || loadingPreview}
                  className="flex-1 py-2.5 rounded-xl bg-amber-600 text-white text-sm font-bold hover:bg-amber-700 disabled:opacity-60 transition-all shadow-md shadow-amber-600/20 flex items-center justify-center gap-2"
                >
                  {offboarding && <Loader className="w-4 h-4 animate-spin" />}
                  {offboarding ? 'Offboarding…' : 'Confirm offboard'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
